// ExperienceForm Component
// Form for managing experience timeline

import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCalendarAlt, FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './ExperienceForm.module.css';
import { useNotification } from '../../../context/NotificationContext';
import { useSports } from '../../../hooks/useSports';

const EXPERIENCE_TYPES = [
  { value: 'club', label: 'Club/Team' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'camp', label: 'Training Camp' },
  { value: 'achievement', label: 'Achievement' },
  { value: 'education', label: 'Education' },
  { value: 'other', label: 'Other' }
];

const ExperienceForm = ({ data, onChange }) => {
  const { showInfo, showSuccess } = useNotification();
  const { sportNames, getEmoji, loading: sportsLoading } = useSports();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'club',
    sport: 'Soccer',
    title: '',
    organization: '',
    location: '',
    startDate: '',
    endDate: '',
    current: false,
    description: '',
    achievements: ''
  });


  // Group experiences by organization
  const groupedExperiences = () => {
    if (!data || data.length === 0) return {};
    
    // First, sort all experiences by start date (newest first)
    const sorted = [...data].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    
    // Group by organization
    const grouped = {};
    sorted.forEach(exp => {
      const org = exp.organization || 'Other Experience';
      if (!grouped[org]) {
        grouped[org] = [];
      }
      grouped[org].push(exp);
    });
    
    return grouped;
  };

  // Calculate total duration at an organization
  const calculateDuration = (experiences) => {
    if (!experiences || experiences.length === 0) return '';
    
    // Find earliest start date and latest end date (or current)
    let earliest = new Date(experiences[0].startDate);
    let latest = null;
    let isCurrent = false;
    
    experiences.forEach(exp => {
      const start = new Date(exp.startDate);
      if (start < earliest) earliest = start;
      
      if (exp.current) {
        isCurrent = true;
      } else if (exp.endDate) {
        const end = new Date(exp.endDate);
        if (!latest || end > latest) latest = end;
      }
    });
    
    const endDate = isCurrent ? new Date() : latest;
    if (!endDate) return '';
    
    // Calculate years and months
    const months = (endDate.getFullYear() - earliest.getFullYear()) * 12 + 
                   (endDate.getMonth() - earliest.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    let duration = '';
    if (years > 0) duration += `${years} yr${years > 1 ? 's' : ''}`;
    if (remainingMonths > 0) {
      if (duration) duration += ' ';
      duration += `${remainingMonths} mo${remainingMonths > 1 ? 's' : ''}`;
    }
    
    return duration;
  };

  // Ensure data exists (after all hooks)
  if (!data) {
    return <div>Loading form...</div>;
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      type: 'club',
      sport: 'Soccer',
      title: '',
      organization: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
      achievements: ''
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title || !formData.startDate || !formData.sport) {
      showInfo('Please fill in required fields (Title, Sport, Start Date)');
      return;
    }

    const updatedData = [...(data || [])];

    if (editingId) {
      // Update existing
      const index = updatedData.findIndex(exp => exp.id === editingId);
      if (index > -1) {
        updatedData[index] = {
          ...formData,
          id: editingId
        };
        showSuccess('Experience updated successfully');
      }
    } else {
      // Add new
      updatedData.push({
        ...formData,
        id: Date.now().toString()
      });
      showSuccess('Experience added successfully');
    }

    // Sort by start date (newest first)
    updatedData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

    onChange(updatedData);
    resetForm();
  };

  // Handle edit
  const handleEdit = (experience) => {
    // Format dates for HTML date inputs (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    // Set form data with properly formatted dates
    const formattedData = {
      ...experience,
      startDate: formatDateForInput(experience.startDate),
      endDate: formatDateForInput(experience.endDate),
      // Ensure all fields exist
      type: experience.type || 'club',
      sport: experience.sport || 'Soccer',
      title: experience.title || '',
      organization: experience.organization || '',
      location: experience.location || '',
      description: experience.description || '',
      achievements: experience.achievements || '',
      current: experience.current || false
    };
    
    setFormData(formattedData);
    setEditingId(experience.id);
    setShowAddForm(true);
  };

  // Handle delete
  const handleDelete = (id) => {
    const updatedData = data.filter(exp => exp.id !== id);
    onChange(updatedData);
    showSuccess('Experience removed');
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear end date if current is checked
    if (field === 'current' && value) {
      setFormData(prev => ({
        ...prev,
        endDate: ''
      }));
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    
    try {
      // Handle different date formats
      let d;
      
      // If it's a simple YYYY-MM-DD format
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-').map(Number);
        d = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Handle ISO format or other formats
        d = new Date(date);
      }
      
      // Check if date is valid
      if (isNaN(d.getTime())) {
        console.warn('Invalid date:', date);
        return 'Invalid Date';
      }
      
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'Invalid Date';
    }
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Experience</h2>
        {!showAddForm && (
          <button
            type="button"
            className={styles.addButton}
            onClick={() => setShowAddForm(true)}
          >
            <FaPlus /> Add Experience
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <form className={styles.experienceForm} onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            {/* Type */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Type *</label>
              <select
                className={styles.select}
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                required
              >
                {EXPERIENCE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sport */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Sport *</label>
              <select
                className={styles.select}
                value={formData.sport}
                onChange={(e) => handleFormChange('sport', e.target.value)}
                required
              >
                {sportNames.map(sport => (
                  <option key={sport} value={sport}>
                    {getEmoji(sport)} {sport}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Title/Role *</label>
              <input
                type="text"
                className={styles.input}
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                placeholder="e.g., Team Captain, Player"
                required
              />
            </div>

            {/* Organization */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Organization/Team</label>
              <input
                type="text"
                className={styles.input}
                value={formData.organization}
                onChange={(e) => handleFormChange('organization', e.target.value)}
                placeholder="e.g., FC Barcelona Academy"
              />
            </div>

            {/* Location */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Location</label>
              <input
                type="text"
                className={styles.input}
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="City, Country"
              />
            </div>

            {/* Dates */}
            <div className={styles.dateGroup}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Start Date *</label>
                <input
                  type="date"
                  className={styles.input}
                  value={formData.startDate}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>End Date</label>
                <input
                  type="date"
                  className={styles.input}
                  value={formData.endDate}
                  onChange={(e) => handleFormChange('endDate', e.target.value)}
                  disabled={formData.current}
                  min={formData.startDate}
                />
              </div>

              <div className={styles.currentCheck}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.current}
                    onChange={(e) => handleFormChange('current', e.target.checked)}
                  />
                  <span className={styles.checkmark}></span>
                  Current
                </label>
              </div>
            </div>

            {/* Description */}
            <div className={styles.formGroup + ' ' + styles.fullWidth}>
              <label className={styles.label}>Description</label>
              <textarea
                className={styles.textarea}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                placeholder="Describe your role, responsibilities, and experience..."
                rows={3}
              />
            </div>

            {/* Achievements */}
            <div className={styles.formGroup + ' ' + styles.fullWidth}>
              <label className={styles.label}>Key Achievements</label>
              <textarea
                className={styles.textarea}
                value={formData.achievements}
                onChange={(e) => handleFormChange('achievements', e.target.value)}
                placeholder="List any notable achievements, awards, or milestones..."
                rows={3}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={resetForm}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitButton}>
              {editingId ? 'Update' : 'Add'} Experience
            </button>
          </div>
        </form>
      )}

      {/* Experience List - LinkedIn Style Grouped by Organization */}
      <div className={styles.experienceTimeline}>
        {data && data.length > 0 ? (
          Object.entries(groupedExperiences()).map(([organization, experiences]) => (
            <div key={organization} className={styles.organizationGroup}>
              {/* Organization Header */}
              <div className={styles.orgHeader}>
                <div className={styles.orgLogo}>
                  <FaBuilding />
                </div>
                <div className={styles.orgInfo}>
                  <h3 className={styles.orgName}>{organization}</h3>
                  <p className={styles.orgDuration}>
                    {calculateDuration(experiences)}
                  </p>
                </div>
              </div>

              {/* Roles/Positions within this organization */}
              <div className={styles.rolesContainer}>
                {experiences.map((experience, index) => (
                  <div key={experience.id} className={styles.roleItem}>
                    {/* Timeline connector */}
                    {experiences.length > 1 && (
                      <div className={styles.timelineConnector}>
                        <div className={styles.timelineDot}></div>
                        {index < experiences.length - 1 && (
                          <div className={styles.timelineLine}></div>
                        )}
                      </div>
                    )}

                    <div className={styles.roleContent}>
                      {/* Role Header */}
                      <div className={styles.roleHeader}>
                        <div className={styles.roleTitle}>
                          <h4>{experience.title}</h4>
                          <div className={styles.roleMeta}>
                            {experience.sport && (
                              <span className={styles.sportTag}>
                                {experience.sport}
                              </span>
                            )}
                            <span className={styles.type}>
                              {EXPERIENCE_TYPES.find(t => t.value === experience.type)?.label}
                            </span>
                            <span className={styles.date}>
                              <FaCalendarAlt />
                              {formatDate(experience.startDate)} - {
                                experience.current ? 'Present' : formatDate(experience.endDate)
                              }
                            </span>
                            {experience.location && (
                              <span className={styles.location}>
                                <FaMapMarkerAlt />
                                {experience.location}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className={styles.roleActions}>
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => handleEdit(experience)}
                            title="Edit experience"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className={styles.iconButton + ' ' + styles.danger}
                            onClick={() => handleDelete(experience.id)}
                            title="Delete experience"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      {/* Description */}
                      {experience.description && (
                        <p className={styles.description}>{experience.description}</p>
                      )}

                      {/* Achievements */}
                      {experience.achievements && (
                        <div className={styles.achievements}>
                          <strong>Key Achievements:</strong>
                          <p>{experience.achievements}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>No experience added yet.</p>
            <p>Add your sports journey to showcase your background.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceForm;
